import React, { useEffect, useState } from 'react';
import { Header, Toast, Image, Button, Items, Icon, Placeholder } from 'react-frontier';
import { Link } from 'react-router-dom';
import { useUser } from '../hooks';
import { relativeTime } from '../SiteUtil';
import API from '../API';
import moment from 'moment';

export interface Notification{
	notification_id: number;
	title: string;
	message: string;
	date_created: number;
	url: string;
	dismissed: boolean;
	date_id: number;
	poster_url?: string;
}

interface NotificationsProps{
	open: boolean;
	onClose: () => void;
}

const PAGE_SIZE = 10;

var NotificationItem = (props: { notification?: Notification, placeholder?: boolean, onClick?: (n: Notification)=>void })=>{
	if(!props.notification && !props.placeholder) return null;
	if(!props.notification && props.placeholder){
		return <div className='item'>
			<div className="image"></div>
			<div className="data">
				<div className="title">
					<Placeholder />
				</div>
				<div className="created">
					<Placeholder height={14} width={20} />
				</div>
			</div>
		</div>
	}

	var onClick = ()=>{
		if(props.onClick) props.onClick(props.notification);
	}

	return <Link to={props.notification.url} key={`NTFID-${props.notification.notification_id}`} onClick={onClick} reloadDocument>
		<div className={`item ${props.notification.dismissed ? 'dismissed' : ''}`}>
			<Image src={props.notification.poster_url || '/placeholder.png'} fallback='/placeholder.png' />
			<div className='data'>
				<div className='title' style={{ fontWeight: 600, marginBottom: 2 }}>{props.notification.title}</div>
				<div className='message' style={{ 
					fontSize: 13, 
					color: '#606060', 
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					lineHeight: '1.4',
					marginBottom: 4
				}}>
					{props.notification.message}
				</div>
				<div className='created' style={{ fontSize: 11, color: '#999' }}>{moment.unix(props.notification.date_created).format('DD/MMM/YY HH:mm')}
					{!props.notification.dismissed && (
						<Icon name='circle' color='red' size={8} style={{ marginLeft: 5, marginRight: 0 }} />
					)}
				</div>
			</div>
			<div className='meta'>													
				<div className='time'>{relativeTime(props.notification.date_created)}</div>
			</div>
		</div>
	</Link>
}

var Notifications = (props: NotificationsProps)=>{
	var { updateNotifications } = useUser();
	var [notifications, setNotifications] = useState<Notification[]>(null);
	var [loading, setLoading] = useState(true);
	var [page, setPage] = useState<number>(0);
	
	useEffect(()=>{
		setNotifications(null);
		setPage(0);
		get(0);
	}, [props.open]);

	var get = async(pageNum: number)=>{
		var offset = pageNum * PAGE_SIZE;

		setLoading(true);
		API.getNotifications(offset, PAGE_SIZE).then(res=>{
			if(res.error) return Toast.error(res.message);
			setNotifications(prev => [...(prev || []), ...res.data]);
			setPage(pageNum);
		}).catch(err=>{
			return Toast.error('Hubo un error cargando las notificaciones (LCL-1)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onDismissNotification = (notification_id: number)=>{
		API.dismissNotification(notification_id).then(res=>{
			if(res.error)return Toast.error(res.message);
			updateNotifications(notifications.filter(a=>!a.dismissed).length-1);
		}).catch(err=>{
			return Toast.error('Error al marcar la notificación como leída (LCL-1)');
		});
	}

	var groupNotificationsByDate = (notifications: Notification[])=>{
		var today = moment().startOf('day');
		var yesterday = moment().subtract(1, 'day').startOf('day');
		
		return notifications.reduce((grouped, notification)=>{
			var notifDate = moment.unix(notification.date_created);
			var key = notifDate.isSameOrAfter(today) ? 'Hoy' 
				: notifDate.isSameOrAfter(yesterday) ? 'Ayer' 
				: 'Antiguas';
			
			if(!grouped[key]) grouped[key] = [];
			grouped[key].push(notification);
			return grouped;
		}, {} as { [key: string]: Notification[] });
	}

	if(!props.open) return null;

	var onItemClick = (n: Notification)=>{
		onDismissNotification(n.notification_id);
		props.onClose();
	}

	return <>
		<div className={`ar notifications-sidebar ${props.open ? 'open' : ''}`} style={{ boxShadow: '-2px 0 10px rgba(0,0,0,0.1)' }}>
			<div className='header' style={{ padding: '15px 20px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<div className='title' style={{ fontSize: 18, fontWeight: 700 }}>Notificaciones</div>
				<div className="close" onClick={props.onClose} style={{ cursor: 'pointer', color: '#666' }}>
					<Icon name='close' />
				</div>
			</div>
			<div className='content' style={{ overflowY: 'auto', height: 'calc(100% - 60px)' }}>
				{!notifications ? <div className="list">
					<div className="date-label" style={{ padding: '15px 20px 5px' }}>
						<Placeholder />
					</div>
					<NotificationItem placeholder />
					<NotificationItem placeholder />
					<NotificationItem placeholder />
					<NotificationItem placeholder />
				</div> : notifications.length === 0 ? (
					<div className='empty' style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 14 }}>
						<div>No tienes notificaciones</div>
					</div>
				) : (
					<div className='list'>
						{Object.entries(groupNotificationsByDate(notifications)).map(([dateGroup, notifs]) => (
							<div key={dateGroup} className='group'>
								<div className='date-label' style={{ 
									padding: '15px 20px 5px', 
									fontSize: 11, 
									fontWeight: 600, 
									color: '#888', 
									textTransform: 'uppercase', 
									letterSpacing: '0.5px' 
								}}>{dateGroup}</div>
								<div className="fr selectable items">
									{notifs.map(a=>(
										<NotificationItem key={a.notification_id} notification={a} onClick={onItemClick} />
									))}
								</div>
							</div>
						))}
						{notifications.length > 0 && notifications.length % PAGE_SIZE === 0 && (
							loading ? (
								<Header text={'Cargando anteriores...'} loading/>
							) : (
								<div className='load-more' style={{ padding: 20, textAlign: 'center' }}>
									<Button text='Cargar más' basic onClick={() => get(page + 1)} />
								</div>
							)
						)}
					</div>
				)}
			</div>
		</div>
	</>
}

export default Notifications;
